(() => {
  class ContactFloater extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-contact-toggle]');
      this.chatButton = this.querySelector('[data-contact-chat-proxy], [data-contact-chat]');
      this.actions = this.querySelector('[data-contact-actions]');
      this.welcomePrompt = this.querySelector('[data-contact-welcome]');
      this.welcomeReply = this.querySelector('[data-contact-welcome-reply]');
      this.welcomeClose = this.querySelector('[data-contact-welcome-close]');
      this.nativeChatHidden = this.dataset.hideNativeChat === 'true';
      this.nativeChatNodes = new Map();
      this.nativeChatShadowObservers = new Map();
      this.nativeChatHoverNode = null;
      this.heroRevealThreshold = null;
      this.footerRevealThreshold = null;
      this.scrollFrame = null;
      this.chatCartStorageKey = 'lbr-chat-cart-monitor';
      this.chatCartCheckPending = false;
      this.chatCartDrawerOpening = false;
      this.chatLayerState = null;
      this.restoreChatAfterDrawer = false;
      this.chatRestorePending = false;
      this.welcomeSeenKey = 'lbr-contact-welcome-seen-v1';
      this.welcomeReady = false;
      this.hasOpenedNativeChat = false;
      this.handleChatTransition = (event) => {
        if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
          this.syncNativeChatProxy();
        }
      };
      this.handleNativeChatHoverStart = () => this.chatButton?.classList.add('is-proxy-hover');
      this.handleNativeChatHoverEnd = () => this.chatButton?.classList.remove('is-proxy-hover');
      this.handleNativeChatActivate = () => {
        if (!this.chatRestorePending) this.armChatCartMonitor();
      };
      this.scheduleNativeChatSync = () => {
        if (this.nativeChatSyncTimeout) return;
        this.nativeChatSyncTimeout = window.setTimeout(() => {
          this.nativeChatSyncTimeout = null;
          this.hideNativeChat();
        }, 250);
      };

      this.toggleButton?.addEventListener('click', () => this.toggle());
      this.chatButton?.addEventListener('click', () => this.openNativeChat());
      this.welcomeReply?.addEventListener('click', this.handleWelcomeReply);
      this.welcomeClose?.addEventListener('click', this.handleWelcomeClose);
      this.chatButton?.addEventListener('transitionend', this.handleChatTransition);
      this.querySelectorAll('[data-contact-action]').forEach((action) => {
        action.addEventListener('click', () => this.close());
      });
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      this.heroRevealThreshold = this.getHeroRevealThreshold();
      this.footerRevealThreshold = this.getFooterRevealThreshold();
      this.handleReveal();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.resumeChatCartMonitor();
      this.chatCartMonitorInterval = window.setInterval(() => this.checkChatCart(), 900);
      this.initializeWelcomePrompt();

      if (this.nativeChatHidden) {
        const deferMonitoring = window.matchMedia('(max-width: 749px)').matches && !window.Shopify?.designMode;
        this.hideNativeChat(!deferMonitoring);

        if (deferMonitoring) {
          this.startNativeChatMonitoringOnInteraction = () => {
            this.removeNativeChatMonitoringTriggers();
            this.startNativeChatMonitoring();
          };
          window.addEventListener('scroll', this.startNativeChatMonitoringOnInteraction, { passive: true, once: true });
          window.addEventListener('pointerdown', this.startNativeChatMonitoringOnInteraction, { passive: true, once: true });
        } else {
          this.startNativeChatMonitoring();
        }
      } else {
        document.documentElement.classList.add('lbr-contact-floater-ready');
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      this.chatButton?.removeEventListener('transitionend', this.handleChatTransition);
      this.welcomeReply?.removeEventListener('click', this.handleWelcomeReply);
      this.welcomeClose?.removeEventListener('click', this.handleWelcomeClose);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.welcomeDelayTimeout) window.clearTimeout(this.welcomeDelayTimeout);
      if (this.welcomeHideTimeout) window.clearTimeout(this.welcomeHideTimeout);
      this.chatObserver?.disconnect();
      if (this.nativeChatHideInterval) window.clearInterval(this.nativeChatHideInterval);
      if (this.nativeChatSyncTimeout) window.clearTimeout(this.nativeChatSyncTimeout);
      this.removeNativeChatMonitoringTriggers();
      if (this.chatCartMonitorInterval) window.clearInterval(this.chatCartMonitorInterval);
      if (this.chatRestoreTimeout) window.clearTimeout(this.chatRestoreTimeout);
      this.cartDrawerLayerObserver?.disconnect();
      this.nativeChatShadowObservers.forEach((observer) => observer.disconnect());
      this.restoreNativeChatLayer();
      this.unbindNativeChatActivation();
      this.unbindNativeChatHover();
      this.nativeChatNodes.forEach((state, node) => this.restoreNativeNode(node, state));
      document.documentElement.classList.remove('lbr-native-chat-open');
    }

    handleDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    handleKeydown = (event) => {
      if (event.key === 'Escape') {
        this.hideWelcomePrompt();
        this.close();
      }
    };

    handleWelcomeReply = () => {
      this.hideWelcomePrompt();
      this.openNativeChat();
    };

    handleWelcomeClose = () => {
      this.hideWelcomePrompt();
    };

    initializeWelcomePrompt() {
      if (!this.welcomePrompt || window.matchMedia('(max-width: 749px)').matches) return;

      try {
        if (!window.Shopify?.designMode && window.sessionStorage.getItem(this.welcomeSeenKey) === 'true') return;
      } catch (error) {
        // Continue with an in-memory one-time display when storage is unavailable.
      }

      const delay = Number.parseInt(this.dataset.welcomeDelay, 10) || 7000;
      this.welcomeDelayTimeout = window.setTimeout(() => {
        this.welcomeReady = true;
        this.showWelcomePrompt();
      }, delay);
    }

    showWelcomePrompt() {
      if (
        !this.welcomeReady ||
        this.hasOpenedNativeChat ||
        !this.welcomePrompt ||
        !this.classList.contains('is-visible') ||
        this.classList.contains('is-open') ||
        window.matchMedia('(max-width: 749px)').matches
      ) return;

      this.welcomeReady = false;
      this.classList.add('is-welcome-visible');
      this.welcomePrompt.setAttribute('aria-hidden', 'false');

      if (!window.Shopify?.designMode) {
        try {
          window.sessionStorage.setItem(this.welcomeSeenKey, 'true');
        } catch (error) {
          // The current component instance still prevents a duplicate display.
        }
      }

      const duration = Number.parseInt(this.dataset.welcomeDuration, 10) || 5000;
      this.welcomeHideTimeout = window.setTimeout(() => this.hideWelcomePrompt(), duration);
    }

    hideWelcomePrompt() {
      if (this.welcomeHideTimeout) {
        window.clearTimeout(this.welcomeHideTimeout);
        this.welcomeHideTimeout = null;
      }
      this.classList.remove('is-welcome-visible');
      this.welcomePrompt?.setAttribute('aria-hidden', 'true');
    }

    cancelWelcomePrompt() {
      this.hasOpenedNativeChat = true;
      this.welcomeReady = false;
      if (this.welcomeDelayTimeout) {
        window.clearTimeout(this.welcomeDelayTimeout);
        this.welcomeDelayTimeout = null;
      }
      this.hideWelcomePrompt();
    }

    handleScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.handleReveal();
      });
    };

    handleResize = () => {
      this.heroRevealThreshold = this.getHeroRevealThreshold();
      this.footerRevealThreshold = this.getFooterRevealThreshold();
      this.handleReveal();
      this.syncNativeChatProxy();
      this.syncRewardsWithNativeChat();
    };

    getHeroRevealThreshold() {
      const mainSection = document.querySelector('main .shopify-section') || document.querySelector('main');
      return mainSection ? mainSection.getBoundingClientRect().height : window.innerHeight;
    }

    getFooterRevealThreshold() {
      const footer = document.querySelector('.footer-wrapper') || document.querySelector('[data-footer-transition]');
      return footer ? footer.getBoundingClientRect().top + window.scrollY : Number.POSITIVE_INFINITY;
    }

    handleReveal() {
      if (window.Shopify?.designMode) {
        this.classList.add('is-visible');
        return;
      }

      const thresholdOffset = window.matchMedia('(max-width: 749px)').matches ? 24 : 0;
      const heroPassed = window.scrollY > this.heroRevealThreshold + thresholdOffset;
      const footerStarted = window.scrollY + window.innerHeight > this.footerRevealThreshold + 40;

      this.classList.toggle('is-visible', heroPassed && !footerStarted);
      this.showWelcomePrompt();
    }

    toggle() {
      this.classList.contains('is-open') ? this.close() : this.open();
    }

    open() {
      // Opening the contact menu counts as an interaction, so do not interrupt it
      // later with the delayed welcome prompt.
      this.cancelWelcomePrompt();
      this.classList.add('is-open');
      this.toggleButton?.setAttribute('aria-expanded', 'true');
      this.syncNativeChatProxy();
    }

    close() {
      this.classList.remove('is-open');
      this.toggleButton?.setAttribute('aria-expanded', 'false');
      this.handleNativeChatHoverEnd();
      this.syncNativeChatProxy();
    }

    bindNativeChatHover(node) {
      if (this.nativeChatHoverNode === node) return;
      this.unbindNativeChatHover();
      node.addEventListener('mouseenter', this.handleNativeChatHoverStart);
      node.addEventListener('mouseleave', this.handleNativeChatHoverEnd);
      node.addEventListener('focus', this.handleNativeChatHoverStart);
      node.addEventListener('blur', this.handleNativeChatHoverEnd);
      this.nativeChatHoverNode = node;
    }

    unbindNativeChatHover() {
      if (!this.nativeChatHoverNode) return;
      this.nativeChatHoverNode.removeEventListener('mouseenter', this.handleNativeChatHoverStart);
      this.nativeChatHoverNode.removeEventListener('mouseleave', this.handleNativeChatHoverEnd);
      this.nativeChatHoverNode.removeEventListener('focus', this.handleNativeChatHoverStart);
      this.nativeChatHoverNode.removeEventListener('blur', this.handleNativeChatHoverEnd);
      this.nativeChatHoverNode = null;
      this.handleNativeChatHoverEnd();
    }

    bindNativeChatActivation(node) {
      if (this.nativeChatActivationNode === node) return;
      this.unbindNativeChatActivation();
      node.addEventListener('click', this.handleNativeChatActivate, true);
      this.nativeChatActivationNode = node;
    }

    unbindNativeChatActivation() {
      if (!this.nativeChatActivationNode) return;
      this.nativeChatActivationNode.removeEventListener('click', this.handleNativeChatActivate, true);
      this.nativeChatActivationNode = null;
    }

    findNativeChat() {
      const targets = this.getNativeChatTargets().filter((node) => node.isConnected && !node.disabled);
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const layoutSelector = isMobile ? '.mobile-layout' : '.desktop-layout';
      const belongsToLayout = (node) => {
        let current = node;

        // Inbox renders the activator inside Shadow DOM, so Element.closest()
        // cannot reach the mobile/desktop layout wrapper on its own.
        while (current) {
          if (current.matches?.(layoutSelector)) return true;
          if (current.parentElement) {
            current = current.parentElement;
            continue;
          }
          const root = current.getRootNode?.();
          current = root instanceof ShadowRoot ? root.host : null;
        }

        return false;
      };
      const layoutTargets = targets.filter(belongsToLayout);
      const preferredTargets = layoutTargets.length ? layoutTargets : targets;

      return preferredTargets.find((node) => !this.nativeChatNodes.has(node)) ||
        preferredTargets[preferredTargets.length - 1] ||
        null;
    }

    getOpenShadowRoots() {
      const roots = [];
      const visit = (root) => {
        roots.push(root);
        root.querySelectorAll('*').forEach((node) => {
          if (node.shadowRoot) visit(node.shadowRoot);
        });
      };

      visit(document);
      return roots;
    }

    getNativeChatTargets() {
      const roots = this.getOpenShadowRoots();
      const activators = roots.flatMap((root) =>
        Array.from(root.querySelectorAll('button.activator')).filter((node) => !this.contains(node))
      );
      if (activators.length) return [...new Set(activators)];

      const chatToggles = roots.flatMap((root) =>
        Array.from(root.querySelectorAll('button.chat-toggle, .chat-app > button')).filter((node) => !this.contains(node))
      );
      return [...new Set(chatToggles)];
    }

    observeNativeChatShadows() {
      this.getOpenShadowRoots().forEach((shadowRoot) => {
        if (shadowRoot === document || this.nativeChatShadowObservers.has(shadowRoot)) return;

        const observer = new MutationObserver(this.scheduleNativeChatSync);
        observer.observe(shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['open', 'aria-expanded']
        });
        this.nativeChatShadowObservers.set(shadowRoot, observer);
      });
    }

    startNativeChatMonitoring() {
      if (this.chatObserver) return;
      this.chatObserver = new MutationObserver(this.scheduleNativeChatSync);
      this.chatObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'aria-expanded']
      });
      this.observeNativeChatShadows();
      // Inbox can replace its launcher after a conversation closes.
      this.nativeChatHideInterval = window.setInterval(() => this.hideNativeChat(), 3000);
    }

    removeNativeChatMonitoringTriggers() {
      if (!this.startNativeChatMonitoringOnInteraction) return;
      window.removeEventListener('scroll', this.startNativeChatMonitoringOnInteraction);
      window.removeEventListener('pointerdown', this.startNativeChatMonitoringOnInteraction);
      this.startNativeChatMonitoringOnInteraction = null;
    }

    hideNativeChat(observeShadows = true) {
      if (observeShadows) this.observeNativeChatShadows();
      this.syncNativeChatProxy();
      const nativeChatTargets = this.getNativeChatTargets();
      nativeChatTargets.forEach((node) => {
        if (node.matches('button.activator') && node.dataset.lbrContactProxy === 'true') return;
        if (!this.nativeChatNodes.has(node)) {
          this.nativeChatNodes.set(node, {
            ariaHidden: node.getAttribute('aria-hidden'),
            display: node.style.display,
            top: node.style.top
          });
        }
        if (node.matches('button.activator')) node.style.setProperty('top', '2000px', 'important');
        else node.style.setProperty('display', 'none', 'important');
        node.setAttribute('aria-hidden', 'true');
      });

      if (nativeChatTargets.length) {
        document.documentElement.classList.add('lbr-contact-floater-ready');
      }

      this.syncRewardsWithNativeChat();
    }

    syncNativeChatProxy() {
      const nativeChat = this.findNativeChat();
      if (!nativeChat || !nativeChat.matches('button.activator')) return;

      const isOpen = this.classList.contains('is-open');
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const side = isMobile ? 16 : 24;
      const bottom = isMobile ? 16 : 24;
      const size = isMobile ? 44 : 48;
      const proxyBottom = bottom + 132;
      const isLeft = this.classList.contains('lbr-contact-floater--left');
      const nudge = nativeChat.closest('.activator-nudge');
      const set = (node, property, value) => node?.style.setProperty(property, value, 'important');
      // Keep the native Inbox activator directly over the custom chat action on
      // every device. Mobile then receives a trusted user tap instead of relying
      // on synthetic events, which Inbox stops accepting after a conversation is
      // minimized.
      const chatRect = isOpen ? this.chatButton?.getBoundingClientRect() : null;
      const hasChatRect = chatRect && chatRect.width > 0 && chatRect.height > 0;
      const proxyWidth = hasChatRect ? chatRect.width : size;
      const proxyHeight = hasChatRect ? chatRect.height : size;
      const useMobileNudgePosition = isMobile && Boolean(nudge);

      nativeChat.dataset.lbrContactProxy = 'true';
      this.bindNativeChatActivation(nativeChat);
      // On mobile, focusing Inbox's activator can fire the visual hover transition
      // while it is opening and interrupt the next activation. Desktop alone needs
      // this proxy hover because the transparent activator overlays the custom button.
      if (isMobile) this.unbindNativeChatHover();
      else this.bindNativeChatHover(nativeChat);
      // Inbox wraps the mobile activator in a positioned nudge element. Position
      // the wrapper at the proxy and keep the button at its local origin; setting
      // the same viewport coordinates on both makes them add together after Inbox
      // remounts (for example after closing the newsletter dialog).
      set(nativeChat, 'position', useMobileNudgePosition ? 'absolute' : 'fixed');
      set(nativeChat, 'display', 'block');
      set(nativeChat, 'width', `${proxyWidth}px`);
      set(nativeChat, 'height', `${proxyHeight}px`);
      set(nativeChat, 'opacity', '0');
      set(nativeChat, 'visibility', 'visible');
      set(nativeChat, 'z-index', '10031');
      set(nativeChat, 'touch-action', 'manipulation');
      set(nativeChat, '-webkit-tap-highlight-color', 'transparent');
      set(nativeChat, 'transform', 'none');
      set(nativeChat, 'margin', '0');

      if (!isOpen) {
        nativeChat.setAttribute('aria-hidden', 'true');
        set(nativeChat, 'top', useMobileNudgePosition ? '0' : '2000px');
        set(nativeChat, 'bottom', 'auto');
        set(nativeChat, 'left', useMobileNudgePosition ? '0' : 'auto');
        set(nativeChat, 'right', 'auto');
        set(nativeChat, 'pointer-events', 'none');
      } else {
        set(nativeChat, 'top', useMobileNudgePosition ? '0' : hasChatRect ? `${chatRect.top}px` : 'auto');
        set(nativeChat, 'bottom', useMobileNudgePosition || hasChatRect ? 'auto' : `${proxyBottom}px`);
        set(nativeChat, 'left', useMobileNudgePosition ? '0' : hasChatRect ? `${chatRect.left}px` : isLeft ? `${side}px` : 'auto');
        set(nativeChat, 'right', useMobileNudgePosition || hasChatRect || isLeft ? 'auto' : `${side}px`);
        set(nativeChat, 'pointer-events', 'auto');
        nativeChat.removeAttribute('aria-hidden');
      }

      if (!nudge) return;
      nudge.dataset.lbrContactProxy = 'true';
      set(nudge, 'position', 'fixed');
      set(nudge, 'display', 'block');
      set(nudge, 'width', `${proxyWidth}px`);
      set(nudge, 'height', `${proxyHeight}px`);
      set(nudge, 'opacity', '0');
      set(nudge, 'visibility', 'visible');
      set(nudge, 'z-index', '10030');
      set(nudge, 'overflow', 'visible');
      set(nudge, 'touch-action', 'manipulation');
      set(nudge, '-webkit-tap-highlight-color', 'transparent');
      set(nudge, 'transform', 'none');
      set(nudge, 'margin', '0');
      set(nudge, 'pointer-events', isOpen ? 'auto' : 'none');
      set(nudge, 'top', isOpen ? (hasChatRect ? `${chatRect.top}px` : 'auto') : '2000px');
      set(nudge, 'bottom', isOpen ? (hasChatRect ? 'auto' : `${proxyBottom}px`) : 'auto');
      set(nudge, 'left', isOpen && hasChatRect ? `${chatRect.left}px` : isLeft ? `${side}px` : 'auto');
      set(nudge, 'right', isOpen && hasChatRect ? 'auto' : isLeft ? 'auto' : `${side}px`);
    }

    restoreNativeNode(node, state) {
      if (!node.isConnected) return;
      if (state.display) node.style.display = state.display;
      else node.style.removeProperty('display');
      if (state.top) node.style.top = state.top;
      else node.style.removeProperty('top');
      if (state.ariaHidden === null) node.removeAttribute('aria-hidden');
      else node.setAttribute('aria-hidden', state.ariaHidden);
    }

    getChatCartMonitor() {
      try {
        const state = JSON.parse(window.sessionStorage.getItem(this.chatCartStorageKey));
        if (!state || !Number.isFinite(state.itemCount) || state.expiresAt <= Date.now()) {
          window.sessionStorage.removeItem(this.chatCartStorageKey);
          return null;
        }
        return state;
      } catch (error) {
        return null;
      }
    }

    setChatCartMonitor(itemCount) {
      try {
        window.sessionStorage.setItem(this.chatCartStorageKey, JSON.stringify({
          itemCount,
          expiresAt: Date.now() + (30 * 60 * 1000)
        }));
      } catch (error) {
        // Session storage can be unavailable in strict privacy modes.
      }
    }

    clearChatCartMonitor() {
      try {
        window.sessionStorage.removeItem(this.chatCartStorageKey);
      } catch (error) {
        // Session storage can be unavailable in strict privacy modes.
      }
    }

    async getCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Unable to read cart: ${response.status}`);
      return response.json();
    }

    async armChatCartMonitor() {
      if (this.chatCartArmPending) return;
      this.chatCartArmPending = true;
      try {
        const cart = await this.getCart();
        this.setChatCartMonitor(cart.item_count);
      } catch (error) {
        console.warn('Unable to monitor cart additions from Shopify Inbox:', error);
      } finally {
        this.chatCartArmPending = false;
      }
    }

    resumeChatCartMonitor() {
      if (!this.getChatCartMonitor()) return;
      window.setTimeout(() => this.checkChatCart(), 250);
    }

    async checkChatCart() {
      const monitor = this.getChatCartMonitor();
      if (!monitor || this.chatCartCheckPending || this.chatCartDrawerOpening) return;

      this.chatCartCheckPending = true;
      try {
        const cart = await this.getCart();
        if (cart.item_count > monitor.itemCount) {
          await this.openCartDrawerForChat(cart);
        } else if (cart.item_count < monitor.itemCount) {
          this.setChatCartMonitor(cart.item_count);
        }
      } catch (error) {
        console.warn('Unable to check Shopify Inbox cart changes:', error);
      } finally {
        this.chatCartCheckPending = false;
      }
    }

    getTopShadowHost(node) {
      let current = node;
      let host = null;

      while (current) {
        const root = current.getRootNode?.();
        if (!(root instanceof ShadowRoot)) break;
        host = root.host;
        current = host;
      }

      return host;
    }

    lowerNativeChatLayer() {
      const nativeChat = this.findNativeChat();
      const host = nativeChat ? this.getTopShadowHost(nativeChat) : null;
      if (!host || this.chatLayerState?.node === host) return;

      this.restoreNativeChatLayer();
      this.chatLayerState = {
        node: host,
        zIndex: host.style.getPropertyValue('z-index'),
        priority: host.style.getPropertyPriority('z-index')
      };
      host.style.setProperty('z-index', '10050', 'important');
    }

    restoreNativeChatLayer() {
      if (!this.chatLayerState) return;
      const { node, zIndex, priority } = this.chatLayerState;
      if (node.isConnected) {
        if (zIndex) node.style.setProperty('z-index', zIndex, priority);
        else node.style.removeProperty('z-index');
      }
      this.chatLayerState = null;
    }

    getNativeChatHost() {
      const nativeChat = this.findNativeChat();
      return nativeChat ? this.getTopShadowHost(nativeChat) : document.querySelector('shopify-agent, shopify-chat');
    }

    isNativeChatOpen() {
      const host = this.getNativeChatHost();
      if (host?.hasAttribute('open') || host?.open === true) return true;

      return this.getNativeChatTargets().some((node) =>
        node.matches('button.activator') && node.getAttribute('aria-expanded') === 'true'
      );
    }

    syncRewardsWithNativeChat() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      document.documentElement.classList.toggle(
        'lbr-native-chat-open',
        isMobile && this.isNativeChatOpen()
      );
    }

    restoreNativeChatAfterDrawer() {
      if (!this.restoreChatAfterDrawer || this.chatRestorePending) return;

      this.chatRestorePending = true;
      let attempts = 0;
      const ensureChatIsOpen = () => {
        if (!this.restoreChatAfterDrawer || this.isNativeChatOpen()) {
          this.restoreChatAfterDrawer = false;
          this.chatRestorePending = false;
          return;
        }

        const inboxApi = window.Shopify?.Inbox || window.Shopify?.inbox || window.Shopify?.chat;
        const nativeChat = this.findNativeChat();
        const nativeChatHost = this.getNativeChatHost();

        if (typeof nativeChatHost?.show === 'function') {
          nativeChatHost.show();
        } else if (nativeChatHost && 'open' in nativeChatHost) {
          nativeChatHost.open = true;
        } else if (attempts === 0 && typeof inboxApi?.open === 'function') {
          inboxApi.open();
        } else if (nativeChat) {
          nativeChat.removeAttribute('aria-hidden');
          nativeChat.click();
        }

        attempts += 1;
        if (attempts < 3) {
          this.chatRestoreTimeout = window.setTimeout(ensureChatIsOpen, attempts === 1 ? 350 : 700);
          return;
        }

        this.restoreChatAfterDrawer = false;
        this.chatRestorePending = false;
        this.hideNativeChat();
      };

      // Mobile Inbox light-dismiss runs after the drawer close handler. Wait for
      // that tap to finish, then restore through the agent API if it was hidden.
      this.chatRestoreTimeout = window.setTimeout(ensureChatIsOpen, 300);
    }

    watchCartDrawerLayer(drawer) {
      this.cartDrawerLayerObserver?.disconnect();
      this.cartDrawerLayerObserver = new MutationObserver(() => {
        if (drawer.getAttribute('aria-hidden') !== 'true') return;
        document.body.classList.remove('lbr-chat-cart-drawer-open');
        this.restoreNativeChatLayer();
        this.restoreNativeChatAfterDrawer();
      });
      this.cartDrawerLayerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    }

    async openCartDrawerForChat(cart) {
      this.chatCartDrawerOpening = true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}?sections=cart-drawer`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!response.ok) throw new Error(`Unable to render cart drawer: ${response.status}`);

        const sections = await response.json();
        await customElements.whenDefined('cart-drawer');
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        this.clearChatCartMonitor();
        this.restoreChatAfterDrawer = true;
        document.body.classList.add('lbr-chat-cart-drawer-open');
        this.lowerNativeChatLayer();
        this.watchCartDrawerLayer(drawer);
        if (typeof drawer.renderCartDrawer === 'function') {
          drawer.renderCartDrawer(sections['cart-drawer']);
        } else {
          window.eventBus?.emit('update:cart:drawer', { sections });
        }
        window.wetheme?.updateCartCount?.(cart);

        window.requestAnimationFrame(() => {
          if (typeof drawer.handleCartOpen === 'function') {
            drawer.handleCartOpen({ scrollToTop: true });
          } else {
            window.eventBus?.emit('open:cart:drawer', { scrollToTop: true });
          }
        });
      } catch (error) {
        console.error('Unable to open the cart drawer after adding from Shopify Inbox:', error);
      } finally {
        this.chatCartDrawerOpening = false;
      }
    }

    triggerNativeChat(node) {
      node.focus?.({ preventScroll: true });
      const eventInit = {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail: 1,
        view: window
      };

      if (typeof window.PointerEvent === 'function') {
        node.dispatchEvent(new PointerEvent('pointerdown', {
          ...eventInit,
          isPrimary: true,
          pointerId: 1,
          pointerType: 'mouse'
        }));
        node.dispatchEvent(new PointerEvent('pointerup', {
          ...eventInit,
          isPrimary: true,
          pointerId: 1,
          pointerType: 'mouse'
        }));
      }

      node.dispatchEvent(new MouseEvent('mousedown', eventInit));
      node.dispatchEvent(new MouseEvent('mouseup', eventInit));
      node.dispatchEvent(new MouseEvent('click', eventInit));
    }

    openNativeChat() {
      this.cancelWelcomePrompt();
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.armChatCartMonitor();
      // Mobile Inbox treats a synthetic pointer sequence as two activations after
      // the panel has been minimized. Keep the activator available briefly and
      // use its native click instead.
      if (isMobile) this.open();

      if (!isMobile) {
        const nativeChatHost = this.getNativeChatHost();
        const inboxApi = window.Shopify?.Inbox || window.Shopify?.inbox || window.Shopify?.chat;

        // Inbox occasionally rejects synthetic mouse events after it remounts its
        // launcher. Its host's open property is stable across those remounts.
        if (nativeChatHost && 'open' in nativeChatHost) {
          nativeChatHost.open = true;
          window.setTimeout(() => {
            if (this.isNativeChatOpen()) {
              this.close();
              this.hideNativeChat();
              return;
            }

            const nativeChat = this.findNativeChat();
            if (nativeChat) this.triggerNativeChat(nativeChat);
          }, 160);
          return;
        }

        if (typeof inboxApi?.open === 'function') {
          inboxApi.open();
          this.close();
          return;
        }
      }

      let attempts = 0;
      const tryOpen = () => {
        const nativeChat = this.findNativeChat();

        if (nativeChat) {
          if (isMobile) {
            nativeChat.removeAttribute('aria-hidden');
            nativeChat.focus?.({ preventScroll: true });
            nativeChat.click();
            window.setTimeout(() => {
              this.close();
              this.hideNativeChat();
            }, 350);
          } else {
            this.triggerNativeChat(nativeChat);
            window.setTimeout(() => {
              this.close();
              this.hideNativeChat();
            }, 100);
          }
          return;
        }

        if (attempts < 20) {
          attempts += 1;
          window.setTimeout(tryOpen, 100);
          return;
        }

        const inboxApi = window.Shopify?.Inbox || window.Shopify?.inbox || window.Shopify?.chat;
        if (typeof inboxApi?.open === 'function') inboxApi.open();
        this.close();
      };

      tryOpen();
    }
  }

  if (!customElements.get('lbr-contact-floater')) {
    customElements.define('lbr-contact-floater', ContactFloater);
  }
})();
